/**
 * TrafficController — effect-only child component for ADS-B traffic.
 *
 * Extracted from MapView.tsx as part of the Claude audit 2026-05-18
 * finding 7 part 2 (component split). Owns its own narrow zustand
 * subscriptions so its effects only re-run when traffic-related state
 * changes, not on every MapView render.
 *
 * Returns null — no DOM output.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { GeoJSONSource } from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useTrafficPrefsStore } from '../../stores/trafficPrefsStore';
import { useTrafficStore } from '../../lib/traffic/store';
import { useGpsStore } from '../../stores/gpsStore';
import { extrapolatePosition } from '../../lib/geo';
import { isAirborne, onAirborneChange } from '../../lib/airborneDetector';
import {
  startInternetTraffic,
  updateInternetTrafficSettings,
} from '../../lib/traffic/sources/internet';
import { startGdl90Traffic } from '../../lib/traffic/sources/gdl90';
import { useLayerVisibility } from '../useLayerEffects';
import { getMapInstance, EMPTY_FC, safeGetSource } from '../MapView';

export function TrafficController() {
  const showTraffic = useLayerVisibilityStore((s) => s.layers.traffic);
  const {
    trafficRangeNm,
    trafficAltFilterFt,
    trafficHideLowPriority,
    trafficFilterOnlyAirborne,
    trafficAutoDisableAboveFt,
  } = useTrafficPrefsStore(
    useShallow((s) => ({
      trafficRangeNm: s.trafficRangeNm,
      trafficAltFilterFt: s.trafficAltFilterFt,
      trafficHideLowPriority: s.trafficHideLowPriority,
      trafficFilterOnlyAirborne: s.trafficFilterOnlyAirborne,
      trafficAutoDisableAboveFt: s.trafficAutoDisableAboveFt,
    })),
  );

  // ADS-B traffic visibility + poller wiring. Toggling the layer ON
  // boots the internet poller; toggling OFF tears it down and clears
  // the source. Settings changes (range / altitude filter / auto-
  // disable) live-update the poller without reboot.
  useLayerVisibility(
    () => getMapInstance(),
    ['traffic-icon', 'traffic-label'],
    showTraffic,
  );
  useEffect(() => {
    if (!showTraffic) {
      updateInternetTrafficSettings({ enabled: false });
      useTrafficStore.getState().clear();
      const map = getMapInstance();
      const src = safeGetSource<GeoJSONSource>(map, 'traffic');
      src?.setData(EMPTY_FC);
      return;
    }
    startInternetTraffic({
      enabled: true,
      rangeNm: trafficRangeNm,
      autoDisableAboveFt: trafficAutoDisableAboveFt,
    });
    startGdl90Traffic();
    updateInternetTrafficSettings({
      enabled: true,
      rangeNm: trafficRangeNm,
      autoDisableAboveFt: trafficAutoDisableAboveFt,
    });
    useTrafficStore.getState().setEnabled(true);
    return () => {
      // Don't fully dispose on prop change — only on unmount/toggle-off.
    };
  }, [showTraffic, trafficRangeNm, trafficAutoDisableAboveFt]);

  // Push traffic targets into the map source on every store change
  // (poll cycle) AND on a 4 Hz animation tick that dead-reckons each
  // target's position forward from its last reported point using GS
  // and track. ADS-B internet feeds only refresh every ~3-5 sec; the
  // wire data sits frozen between polls. Dead reckoning means targets
  // visually move continuously at ≈60 fps-equivalent perceived speed
  // while the underlying authoritative data still updates on the
  // poll cycle. ForeFlight does the same; without it, aircraft look
  // like they teleport every 5 seconds.
  //
  // Authoritative data lives in useTrafficStore.targets. We compute
  // visible-target metadata once per poll (filters + color class) and
  // store it in a ref; the RAF tick reads the ref and writes
  // extrapolated geometry into the map source. Position smoothing
  // (lerp from extrapolated → truth over 500ms) keeps the snap-back
  // when fresh data arrives from looking like a jump.
  useEffect(() => {
    // (Codex audit 2026-05-18, finding 2) Bail out early when the
    // traffic layer is hidden. The previous effect ran a 4 Hz timer
    // PLUS subscribed to GPS and traffic store updates even with
    // showTraffic=false — building GeoJSON FeatureCollections and
    // calling source.setData() four times per second for nothing.
    if (!showTraffic) return;
    interface VisibleTarget {
      icaoHex: string;
      // Authoritative position from the last poll. We extrapolate FROM
      // this; the next poll overwrites it.
      anchorLat: number;
      anchorLon: number;
      // Position we snapped to when the last frame's smoothing window
      // closed. We lerp from here to the new anchor over 500ms after
      // each fresh poll to avoid visible jumps when the extrapolated
      // position differs from the upstream truth.
      smoothFromLat: number;
      smoothFromLon: number;
      anchorMs: number;
      // Smoothing target reached at anchorMs + 500ms.
      smoothTargetMs: number;
      groundspeedKt: number;
      trackDeg: number;
      callsign: string;
      registration: string;
      colorClass: string;
      altText: string;
      source: string;
    }

    let visibleRef: VisibleTarget[] = [];
    let rafId: number | null = null;
    let tickTimer: ReturnType<typeof setInterval> | null = null;

    function recomputeVisible(): void {
      const fix = useGpsStore.getState().fix;
      const ownAltFt = fix?.altMeters != null ? fix.altMeters * 3.28084 : null;
      const airborne = isAirborne();
      const filterAlt = trafficFilterOnlyAirborne ? (airborne ? trafficAltFilterFt : 0) : trafficAltFilterFt;
      const targets = Array.from(useTrafficStore.getState().targets.values());
      const next: VisibleTarget[] = [];
      // Preserve smoothFrom from the previous batch so each target
      // continues its lerp instead of teleporting on every poll.
      const prevByHex = new Map<string, VisibleTarget>();
      for (const v of visibleRef) prevByHex.set(v.icaoHex, v);
      for (const t of targets) {
        if (
          filterAlt > 0 &&
          ownAltFt != null &&
          t.altBaroFt != null &&
          Math.abs(t.altBaroFt - ownAltFt) > filterAlt
        ) continue;
        if (trafficHideLowPriority) {
          if (
            (t.groundspeedKt != null && t.groundspeedKt < 5) &&
            (t.altBaroFt == null || t.altBaroFt < 500)
          ) continue;
          if (t.category && /^C[0-3]$/.test(t.category)) continue;
        }
        const deltaFt =
          ownAltFt != null && t.altBaroFt != null
            ? t.altBaroFt - ownAltFt : null;
        const colorClass =
          t.emergency !== 'none' ? 'emergency'
          : deltaFt == null ? 'coalt'
          : deltaFt > 500 ? 'above'
          : deltaFt > 200 ? 'near-above'
          : deltaFt < -500 ? 'below'
          : deltaFt < -200 ? 'near-below'
          : 'coalt';
        const altText =
          t.altBaroFt != null ? `FL${Math.round(t.altBaroFt / 100).toString().padStart(3, '0')}` : '';

        const prev = prevByHex.get(t.icaoHex);
        // Compute where the previous frame would have placed this
        // target *right now*. This is the position the user is
        // currently seeing; smoothing lerps from here to the new
        // authoritative anchor.
        let smoothFromLat = t.lat;
        let smoothFromLon = t.lon;
        if (prev) {
          const extrap = extrapolatePosition(
            prev.anchorLat, prev.anchorLon,
            prev.groundspeedKt, prev.trackDeg,
            Date.now() - prev.anchorMs,
          );
          smoothFromLat = extrap.lat;
          smoothFromLon = extrap.lon;
        }

        next.push({
          icaoHex: t.icaoHex,
          anchorLat: t.lat,
          anchorLon: t.lon,
          smoothFromLat,
          smoothFromLon,
          anchorMs: Date.now(),
          smoothTargetMs: Date.now() + 500,
          groundspeedKt: t.groundspeedKt ?? 0,
          trackDeg: t.trackDeg ?? 0,
          callsign: t.callsign ?? '',
          registration: t.registration ?? '',
          colorClass,
          altText,
          source: t.source,
        });
      }
      visibleRef = next;
    }

    function renderFrame(): void {
      const map = getMapInstance();
      if (!map) return;
      const src = safeGetSource<GeoJSONSource>(map, 'traffic');
      if (!src) return;
      const nowMs = Date.now();
      const features: GeoJSON.Feature[] = [];
      for (const v of visibleRef) {
        const elapsedMs = nowMs - v.anchorMs;
        // Don't dead-reckon stale targets beyond 30 sec; they should
        // either get refreshed by a new poll or age out via gc().
        if (elapsedMs > 30_000) continue;
        // Extrapolated position from the anchor using GS + track.
        const extrap = extrapolatePosition(
          v.anchorLat, v.anchorLon, v.groundspeedKt, v.trackDeg, elapsedMs,
        );
        // Smooth from smoothFrom → extrapolated over the first 500ms
        // after each poll so a fresh anchor doesn't teleport the icon.
        const smoothMs = v.smoothTargetMs - v.anchorMs;
        const lerpT = Math.min(1, Math.max(0, elapsedMs / smoothMs));
        const lat = v.smoothFromLat + (extrap.lat - v.smoothFromLat) * lerpT;
        const lon = v.smoothFromLon + (extrap.lon - v.smoothFromLon) * lerpT;
        features.push({
          type: 'Feature',
          properties: {
            icaoHex: v.icaoHex,
            callsign: v.callsign,
            registration: v.registration,
            trackDeg: v.trackDeg,
            colorClass: v.colorClass,
            source: v.source,
            altText: v.altText,
          },
          geometry: { type: 'Point', coordinates: [lon, lat] },
        });
      }
      src.setData({ type: 'FeatureCollection', features });
    }

    function scheduleFrame(): void {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        renderFrame();
      });
    }

    // Initial population, then a 4 Hz render tick (250 ms) for smooth
    // motion. 4 Hz is plenty: at FL350 / 480 kt, that's ≈4 pixels of
    // motion per tick at typical en-route zoom, well under the
    // animation budget and visually indistinguishable from 60 Hz.
    recomputeVisible();
    scheduleFrame();
    tickTimer = setInterval(scheduleFrame, 250);

    const unsubTraffic = useTrafficStore.subscribe(() => {
      recomputeVisible();
      scheduleFrame();
    });
    const unsubGps = useGpsStore.subscribe(() => {
      recomputeVisible();
      scheduleFrame();
    });
    const unsubAir = onAirborneChange((next, prev) => {
      if (next !== prev) {
        recomputeVisible();
        scheduleFrame();
      }
    });
    return () => {
      if (tickTimer) clearInterval(tickTimer);
      if (rafId != null) cancelAnimationFrame(rafId);
      unsubTraffic();
      unsubGps();
      unsubAir();
    };
  }, [showTraffic, trafficAltFilterFt, trafficHideLowPriority, trafficFilterOnlyAirborne]);

  return null;
}
